/**
 * Centralized query key factory for React Query
 * Ensures consistent cache keys across the application
 */

import type { Session, User } from '../types'

/** User query filters */
interface UserQueryFilters {
  role?: User['role']
  approved?: boolean
  status?: User['status']
}

/** Session query filters */
interface SessionQueryFilters {
  userId?: string
  startDate?: string
  endDate?: string
  status?: Session['status']
}

/** Shift query filters */
interface ShiftQueryFilters {
  userId?: string
  startDate?: string
  endDate?: string
}

/** Audit log query filters */
interface AuditQueryFilters {
  collection?: string
  userId?: string
  startDate?: string
  endDate?: string
}

/**
 * Query key factory
 * Using factory pattern for type-safe, consistent query keys
 */
export const queryKeys = {
  // ============================================
  // Users
  // ============================================
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters: UserQueryFilters) => [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    pending: () => [...queryKeys.users.all, 'pending'] as const,
    employees: () => [...queryKeys.users.all, 'employees'] as const,
    activeCount: () => [...queryKeys.users.all, 'activeCount'] as const,
  },

  // ============================================
  // Sessions
  // ============================================
  sessions: {
    all: ['sessions'] as const,
    lists: () => [...queryKeys.sessions.all, 'list'] as const,
    list: (filters: SessionQueryFilters) => [...queryKeys.sessions.lists(), filters] as const,
    details: () => [...queryKeys.sessions.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.sessions.details(), id] as const,
    today: (userId: string) => [...queryKeys.sessions.all, 'today', userId] as const,
    active: (userId: string) => [...queryKeys.sessions.all, 'active', userId] as const,
    userSessions: (userId: string) => [...queryKeys.sessions.all, 'user', userId] as const,
  },

  // ============================================
  // Shifts
  // ============================================
  shifts: {
    all: ['shifts'] as const,
    lists: () => [...queryKeys.shifts.all, 'list'] as const,
    list: (filters: ShiftQueryFilters) => [...queryKeys.shifts.lists(), filters] as const,
    user: (userId: string) => [...queryKeys.shifts.all, 'user', userId] as const,
    week: (weekStart: string) => [...queryKeys.shifts.all, 'week', weekStart] as const,
  },

  // ============================================
  // Notifications
  // ============================================
  notifications: {
    all: ['notifications'] as const,
    user: (userId: string) => [...queryKeys.notifications.all, userId] as const,
    unread: (userId: string) => [...queryKeys.notifications.all, 'unread', userId] as const,
    count: (userId: string) => [...queryKeys.notifications.all, 'count', userId] as const,
  },

  // ============================================
  // Documents
  // ============================================
  documents: {
    all: ['documents'] as const,
    user: (userId: string) => [...queryKeys.documents.all, userId] as const,
  },

  // ============================================
  // Audit Logs
  // ============================================
  audit: {
    all: ['audit'] as const,
    lists: () => [...queryKeys.audit.all, 'list'] as const,
    list: (filters: AuditQueryFilters) => [...queryKeys.audit.lists(), filters] as const,
  },

  // ============================================
  // Analytics
  // ============================================
  analytics: {
    all: ['analytics'] as const,
    daily: (startDate: string, endDate: string) =>
      [...queryKeys.analytics.all, 'daily', startDate, endDate] as const,
    workers: (startDate: string, endDate: string) =>
      [...queryKeys.analytics.all, 'workers', startDate, endDate] as const,
    patterns: (startDate: string, endDate: string) =>
      [...queryKeys.analytics.all, 'patterns', startDate, endDate] as const,
    status: (startDate: string, endDate: string) =>
      [...queryKeys.analytics.all, 'status', startDate, endDate] as const,
    comparative: (startDate: string, endDate: string) =>
      [...queryKeys.analytics.all, 'comparative', startDate, endDate] as const,
  },

  // ============================================
  // Sites
  // ============================================
  sites: {
    all: ['sites'] as const,
    list: () => [...queryKeys.sites.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.sites.all, 'detail', id] as const,
  },

  // ============================================
  // Admin Dashboard (combined stats)
  // ============================================
  adminStats: ['admin', 'stats'] as const,
} as const

export type QueryKeys = typeof queryKeys
